import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, markQuizCompleted, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'deap-activite9';

const DEAPActivite9Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const savedProgress = getActivityProgress(ACTIVITY_ID);
    if (savedProgress && savedProgress.lastViewedSlide > 0) {
      // Resume from last position optionally
    }
  }, []);

  const slides = [
    // Slide 1 - Titre
    {
      id: 1,
      type: 'title',
      title: "Activité 9",
      subtitle: "Repérage et traitement des anomalies et dysfonctionnements",
      description: "En lien avec l'entretien des locaux et des matériels liés aux activités de soins"
    },
    // Slide 2 - Sommaire
    {
      id: 2,
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Introduction et enjeux professionnels" },
        { num: "2", title: "Cadre réglementaire et responsabilité" },
        { num: "3", title: "Rôle de l'auxiliaire de puériculture" },
        { num: "4", title: "Principes fondamentaux de sécurité" },
        { num: "5", title: "Vérification du frigo" },
        { num: "6", title: "Gestion des stocks" },
        { num: "7", title: "Machines à laver et sèche-linge" },
        { num: "8", title: "Surveillance des médicaments" },
        { num: "9", title: "Produits d'entretien" },
        { num: "10", title: "Le plan de change" },
        { num: "11", title: "Autres matériels à surveiller" },
        { num: "12", title: "Hygiène des locaux et déchets" },
        { num: "13", title: "Traçabilité et limites de compétences" }
      ]
    },
    // SECTION 1 - Introduction
    {
      id: 3,
      type: 'section',
      section: "1",
      title: "Introduction et enjeux professionnels"
    },
    {
      id: 4,
      type: 'warning',
      title: "Enjeux de l'environnement matériel",
      content: "L'environnement matériel participe directement à la qualité des soins. Un frigo mal réglé, un plan de change instable ou une machine défectueuse peuvent entraîner :",
      bullets: [
        "Risque infectieux",
        "Risque alimentaire",
        "Risque d'accident",
        "Risque juridique"
      ],
      warningColor: "#DC2626"
    },
    {
      id: 5,
      type: 'highlight',
      title: "Objectif de l'Activité 9",
      content: "L'activité 9 ne consiste pas à réparer, mais à :",
      items: [
        { icon: "👁️", text: "REPÉRER les anomalies" },
        { icon: "🛡️", text: "SÉCURISER immédiatement" },
        { icon: "📢", text: "SIGNALER à la hiérarchie" },
        { icon: "📝", text: "TRACER par écrit" }
      ]
    },
    // SECTION 2 - Cadre réglementaire
    {
      id: 6,
      type: 'section',
      section: "2",
      title: "Cadre réglementaire et responsabilité"
    },
    {
      id: 7,
      type: 'content',
      title: "Obligation de sécurité",
      content: "L'établissement a une obligation de sécurité. Chaque professionnel participe à cette sécurité.",
      subtitle: "La traçabilité protège :",
      bullets: [
        "L'enfant",
        "L'équipe",
        "L'établissement",
        "Le professionnel"
      ],
      highlight: "Ne pas signaler une anomalie engage la responsabilité."
    },
    // SECTION 3 - Rôle de l'AP
    {
      id: 8,
      type: 'section',
      section: "3",
      title: "Rôle précis de l'auxiliaire de puériculture"
    },
    {
      id: 9,
      type: 'checklist',
      title: "Rôle de l'auxiliaire de puériculture",
      content: "L'auxiliaire de puériculture :",
      checks: [
        "Observe quotidiennement",
        "Vérifie selon les protocoles",
        "Repère les dysfonctionnements",
        "Sécurise immédiatement",
        "Informe la hiérarchie",
        "Trace les incidents"
      ],
      note: "Elle ne se substitue pas à un technicien."
    },
    // SECTION 4 - Principes de sécurité
    {
      id: 10,
      type: 'section',
      section: "4",
      title: "Principes fondamentaux de sécurité"
    },
    {
      id: 11,
      type: 'dosDonts',
      title: "Règles de sécurité",
      content: "On ne se met jamais en danger.",
      donts: [
        "Pas d'intervention électrique",
        "Pas de démontage",
        "Pas de réparation improvisée",
        "Pas d'utilisation d'un matériel douteux"
      ],
      dos: [
        "Porter les EPI",
        "Respecter les fiches techniques",
        "Demander de l'aide"
      ]
    },
    // SECTION 5 - Vérification du frigo
    {
      id: 12,
      type: 'section',
      section: "5",
      title: "Vérification du frigo"
    },
    {
      id: 13,
      type: 'imageSlide',
      title: "Contrôle de la température",
      subtitle: "Maintenir la chaîne du froid (≤ 4°C)",
      image: "/images/thermometre_frigo.png",
      imageAlt: "Thermomètre dans un réfrigérateur",
      bullets: [
        "Lire l'affichage extérieur",
        "Vérifier le thermomètre interne",
        "Noter la température sur la fiche de suivi",
        "Contrôler les dates de péremption",
        "Vérifier l'organisation (pas de surcharge)",
        "Vérifier l'état des joints"
      ]
    },
    {
      id: 14,
      type: 'rythme',
      title: "Rythme des vérifications",
      items: [
        { label: "Température", freq: "Matin et soir", color: "#EF4444" },
        { label: "Contrôle visuel", freq: "Quotidien", color: "#F59E0B" },
        { label: "Nettoyage", freq: "Hebdomadaire", color: "#10B981" }
      ]
    },
    {
      id: 15,
      type: 'scenario',
      title: "En cas d'anomalie",
      scenario: "Exemple : température à 8°C",
      steps: [
        "Ne pas ignorer",
        "Prévenir immédiatement",
        "Mettre les denrées en sécurité si nécessaire",
        "Tracer l'incident",
        "Demander intervention technique"
      ]
    },
    // SECTION 6 - Gestion des stocks
    {
      id: 16,
      type: 'section',
      section: "6",
      title: "Gestion des stocks"
    },
    {
      id: 17,
      type: 'content',
      title: "Vérifications des stocks",
      bullets: [
        "Quantités suffisantes",
        "Dates de péremption",
        "Emballages intacts",
        "Rotation des produits (FIFO)",
        "Produits dangereux hors de portée"
      ],
      rythme: [
        { label: "Vérification rapide", freq: "Quotidienne" },
        { label: "Inventaire structuré", freq: "Mensuel" }
      ]
    },
    // SECTION 7 - Machines
    {
      id: 18,
      type: 'section',
      section: "7",
      title: "Machines à laver et sèche-linge"
    },
    {
      id: 19,
      type: 'imageSlide',
      title: "Entretien des machines",
      subtitle: "Prévention incendie, hygiène du linge, durabilité du matériel",
      image: "/images/filtre_seche_linge.png",
      imageAlt: "Filtre de sèche-linge encrassé",
      bullets: [
        "Nettoyer filtre sèche-linge après chaque cycle",
        "Vérifier absence de fuite",
        "Surveiller bruit anormal",
        "Contrôler température atteinte"
      ]
    },
    {
      id: 20,
      type: 'warning',
      title: "En cas de dysfonctionnement",
      content: "Odeur de brûlé ?",
      bullets: [
        "Arrêter immédiatement",
        "Ne pas relancer",
        "Signaler à la hiérarchie",
        "Tracer l'incident"
      ],
      warningColor: "#DC2626"
    },
    // SECTION 8 - Médicaments
    {
      id: 21,
      type: 'section',
      section: "8",
      title: "Surveillance des médicaments"
    },
    {
      id: 22,
      type: 'imageSlide',
      title: "Vérification de l'armoire à pharmacie",
      image: "/images/armoire_pharmacie.png",
      imageAlt: "Armoire à pharmacie sécurisée",
      bullets: [
        "Armoire fermée à clé",
        "Respect des températures de stockage",
        "Dates de péremption vérifiées",
        "Présence des ordonnances",
        "Traçabilité des ouvertures"
      ],
      rythme: "Vérification visuelle quotidienne, contrôle mensuel des dates"
    },
    // SECTION 9 - Produits d'entretien
    {
      id: 23,
      type: 'section',
      section: "9",
      title: "Produits d'entretien"
    },
    {
      id: 24,
      type: 'content',
      title: "Vérifications des produits d'entretien",
      bullets: [
        "Étiquetage lisible",
        "Bouchons fermés",
        "Stockage sécurisé (hors de portée des enfants)",
        "Fiches de données de sécurité disponibles",
        "Aucun mélange dangereux"
      ],
      highlight: "Respecter les pictogrammes de danger CLP"
    },
    // SECTION 10 - Plan de change
    {
      id: 25,
      type: 'section',
      section: "10",
      title: "Le plan de change"
    },
    {
      id: 26,
      type: 'content',
      title: "Entretien du plan de change",
      subtitle: "Après chaque change :",
      bullets: [
        "Nettoyage de la surface",
        "Désinfection",
        "Séchage"
      ]
    },
    {
      id: 27,
      type: 'checklist',
      title: "Points de vérification",
      checks: [
        "Stabilité du plan",
        "Vis bien serrées",
        "Absence de fissure",
        "Roulettes fonctionnelles (si présentes)"
      ],
      highlight: "Si instable : Mettre hors service immédiatement, signaler et tracer."
    },
    // SECTION 11 - Autres matériels
    {
      id: 28,
      type: 'section',
      section: "11",
      title: "Autres matériels à surveiller"
    },
    {
      id: 29,
      type: 'grid',
      title: "Matériels à contrôler régulièrement",
      items: [
        { name: "Thermomètres", check: "Piles, désinfection" },
        { name: "Lits à barreaux", check: "Barreaux stables, espacement conforme" },
        { name: "Escaliers plan de change", check: "Antidérapant en bon état" },
        { name: "Tapis de motricité", check: "Pas déchiré, propre" },
        { name: "Poussettes", check: "Freins fonctionnels" }
      ]
    },
    // SECTION 12 - Hygiène et déchets
    {
      id: 30,
      type: 'section',
      section: "12",
      title: "Hygiène des locaux et gestion des déchets"
    },
    {
      id: 31,
      type: 'twoColumns',
      title: "Hygiène et déchets",
      column1: {
        title: "Hygiène des locaux",
        items: [
          "Sol propre et sec",
          "Pas d'objet au sol",
          "Aération quotidienne",
          "Absence de moisissures",
          "Bon éclairage"
        ]
      },
      column2: {
        title: "Gestion des déchets",
        items: [
          "Tri respecté",
          "Poubelles fermées",
          "Vidage quotidien",
          "DASRI si nécessaire"
        ]
      }
    },
    // SECTION 13 - Traçabilité et limites
    {
      id: 32,
      type: 'section',
      section: "13",
      title: "Traçabilité et limites de compétences"
    },
    {
      id: 33,
      type: 'content',
      title: "Traçabilité - Toujours noter :",
      bullets: [
        "Date et heure",
        "Nature du problème",
        "Action réalisée",
        "Personne informée"
      ],
      note: "La traçabilité protège le professionnel : elle prouve l'action réalisée."
    },
    {
      id: 34,
      type: 'highlight',
      title: "Limites de compétences",
      content: "L'auxiliaire de puériculture n'est pas technicienne. Elle :",
      items: [
        { icon: "✅", text: "Observe" },
        { icon: "✅", text: "Sécurise" },
        { icon: "✅", text: "Signale" },
        { icon: "❌", text: "Ne répare pas les installations électriques ni mécaniques" }
      ]
    },
    // Conclusion
    {
      id: 35,
      type: 'conclusion',
      title: "Conclusion",
      content: "L'activité 9 démontre :",
      bullets: [
        "La vigilance",
        "La prévention",
        "La rigueur",
        "Le respect des protocoles",
        "La protection de l'enfant et du professionnel"
      ],
      finalNote: "Le repérage et le traitement des anomalies sont essentiels à la sécurité de tous."
    }
  ];

  const quizQuestions = [
    { id: 1, question: "Quel est l'objectif principal de l'activité 9 ?", options: ["Réparer les équipements", "Assurer la sécurité et le bon fonctionnement du matériel et des locaux", "Nettoyer les locaux"], correct: 1 },
    { id: 2, question: "À quelle température maximale doit être un frigo de soins ?", options: ["8°C", "4°C", "6°C"], correct: 1 },
    { id: 3, question: "À quelle fréquence vérifier la température du frigo ?", options: ["Une fois par semaine", "1 à 2 fois par jour", "Une fois par mois"], correct: 1 },
    { id: 4, question: "Que faire si le frigo est à 8°C ?", options: ["Attendre qu'il refroidisse", "Signaler immédiatement et tracer", "Baisser le thermostat soi-même"], correct: 1 },
    { id: 5, question: "Qui intervient pour réparer une machine défectueuse ?", options: ["L'auxiliaire de puériculture", "Un technicien", "L'infirmière"], correct: 1 },
    { id: 6, question: "Pourquoi nettoyer le filtre du sèche-linge ?", options: ["Pour économiser l'énergie", "Prévention incendie", "Pour sécher plus vite"], correct: 1 },
    { id: 7, question: "Que vérifier dans une armoire à médicaments ?", options: ["La couleur des boîtes", "Fermeture à clé, dates, températures", "Le nombre de boîtes"], correct: 1 },
    { id: 8, question: "Que signifie 'tracer' ?", options: ["Dessiner un schéma", "Noter par écrit l'anomalie et l'action menée", "Suivre quelqu'un"], correct: 1 },
    { id: 9, question: "Peut-on démonter un appareil électrique ?", options: ["Oui, si on sait faire", "Non", "Oui, si c'est urgent"], correct: 1 },
    { id: 10, question: "Quand nettoyer le plan de change ?", options: ["Une fois par jour", "Après chaque change", "Une fois par semaine"], correct: 1 },
    { id: 11, question: "Pourquoi faire la rotation des stocks ?", options: ["Pour ranger plus facilement", "Éviter les périmés", "Par habitude"], correct: 1 },
    { id: 12, question: "Que faire si une vis du plan de change est desserrée ?", options: ["La resserrer soi-même", "Mettre hors service et signaler", "Continuer à l'utiliser"], correct: 1 },
    { id: 13, question: "Les produits d'entretien doivent-ils être accessibles aux enfants ?", options: ["Oui", "Non", "Parfois"], correct: 1 },
    { id: 14, question: "Que vérifier sur une poussette ?", options: ["La couleur", "Les freins", "La marque"], correct: 1 },
    { id: 15, question: "Pourquoi porter des EPI ?", options: ["Pour être reconnu", "Se protéger", "Par obligation uniquement"], correct: 1 },
    { id: 16, question: "À quelle fréquence contrôler les dates des médicaments ?", options: ["Tous les jours", "Au moins mensuellement", "Tous les ans"], correct: 1 },
    { id: 17, question: "Que faire en cas d'odeur de brûlé sur une machine ?", options: ["Continuer l'utilisation", "Arrêter immédiatement", "Augmenter la ventilation"], correct: 1 },
    { id: 18, question: "Pourquoi aérer les locaux ?", options: ["Pour faire du bruit", "Renouveler l'air et limiter microbes", "Par habitude"], correct: 1 },
    { id: 19, question: "Pourquoi la traçabilité protège le professionnel ?", options: ["Elle fait perdre du temps", "Elle prouve l'action réalisée", "Elle n'est pas utile"], correct: 1 },
    { id: 20, question: "L'AP est-elle habilitée à réparer ?", options: ["Oui", "Non", "Parfois"], correct: 1 }
  ];

  const API_URL = window.location.origin || '';

  const transformTextForTTS = (text) => {
    if (!text) return '';
    let transformed = text
      .replace(/\bAP\b/g, 'auxiliaire de puériculture')
      .replace(/\bDEAP\b/g, 'diplôme d\'état d\'auxiliaire de puériculture')
      .replace(/\bDASRI\b/g, 'déchets d\'activités de soins à risques infectieux')
      .replace(/\bEPI\b/g, 'équipements de protection individuelle')
      .replace(/\bFIFO\b/g, 'premier entré premier sorti')
      .replace(/\bCLP\b/g, 'classification étiquetage emballage');
    return transformed;
  };

  const speakText = async (text) => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const transformedText = transformTextForTTS(text);
      const truncatedText = transformedText.substring(0, 800);
      
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: truncatedText, voice: 'nova', speed: 1.1 })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.audio_url) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = new Audio(data.audio_url);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        } else if (data.audio_base64) {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          audioRef.current = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        }
      } else {
        toast.error("Erreur lors de la génération audio");
      }
    } catch (error) {
      toast.error("Erreur de connexion au service audio");
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const getSlideText = (slide) => {
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.content) text += '. ' + slide.content;
    if (slide.bullets) text += '. ' + slide.bullets.join('. ');
    if (slide.note) text += '. ' + slide.note;
    if (slide.highlight) text += '. ' + slide.highlight;
    if (slide.checks) text += '. ' + slide.checks.join('. ');
    return text;
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      markSlideViewed(ACTIVITY_ID, newSlide, slides.length);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    markSlideViewed(ACTIVITY_ID, index, slides.length);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) {
      setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
    }
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => {
      if (quizAnswers[q.id] === q.correct) score++;
    });
    setQuizScore(score);
    setQuizSubmitted(true);
    markQuizCompleted(ACTIVITY_ID, score, quizQuestions.length);
    toast.success(`Quiz terminé ! Score: ${score}/${quizQuestions.length}`);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-8" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl">{slide.subtitle}</p>
            <p className="text-lg text-white/80 mt-4">{slide.description}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-w-5xl mx-auto">
              {slide.sections.map((section, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm border border-purple-100 cursor-pointer hover:bg-purple-50 transition-colors"
                  onClick={() => goToSlide(Math.min(idx * 2 + 2, slides.length - 1))}
                >
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {section.num}
                  </span>
                  <span className="text-gray-700 text-xs">{section.title}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
            <div className="text-6xl font-bold text-white/30 mb-4">{slide.section}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center px-8">{slide.title}</h2>
          </div>
        );

      case 'content':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.subtitle && <p className="text-purple-700 font-medium mb-3">{slide.subtitle}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.highlight && (
              <div className="mt-4 p-4 bg-amber-100 border-l-4 border-amber-500 rounded">
                <p className="text-amber-800 font-medium">{slide.highlight}</p>
              </div>
            )}
            {slide.note && <p className="mt-4 text-sm text-purple-600 italic">{slide.note}</p>}
            {slide.rythme && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {slide.rythme.map((r, idx) => (
                  <div key={idx} className="p-2 bg-purple-50 rounded text-sm">
                    <span className="font-medium">{r.label}:</span> {r.freq}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'warning':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: `linear-gradient(180deg, ${slide.warningColor}10 0%, #FFFFFF 100%)` }}>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={28} style={{ color: slide.warningColor }} />
              <h2 className="text-2xl font-bold" style={{ color: slide.warningColor }}>{slide.title}</h2>
            </div>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <XCircle size={18} style={{ color: slide.warningColor }} className="flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );

      case 'highlight':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-6">{slide.content}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {slide.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-purple-100">
                  <span className="text-3xl">{item.icon}</span>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'checklist':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            <div className="space-y-2">
              {slide.checks.map((check, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-purple-100">
                  <CheckCircle className="text-green-500" size={20} />
                  <span className="text-gray-700">{check}</span>
                </div>
              ))}
            </div>
            {slide.highlight && (
              <div className="mt-4 p-4 bg-amber-100 border-l-4 border-amber-500 rounded">
                <p className="text-amber-800 font-medium">{slide.highlight}</p>
              </div>
            )}
            {slide.note && <p className="mt-4 text-sm text-purple-600 italic">{slide.note}</p>}
          </div>
        );

      case 'dosDonts':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-2">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h3 className="font-bold text-red-700 mb-3 flex items-center gap-2">
                  <XCircle size={20} /> À ne pas faire
                </h3>
                <ul className="space-y-2">
                  {slide.donts.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-red-700">
                      <span>❌</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h3 className="font-bold text-green-700 mb-3 flex items-center gap-2">
                  <CheckCircle size={20} /> À faire
                </h3>
                <ul className="space-y-2">
                  {slide.dos.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-green-700">
                      <span>✅</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'imageSlide':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-2">{slide.title}</h2>
            {slide.subtitle && <p className="text-purple-600 mb-3">{slide.subtitle}</p>}
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="md:w-1/2">
                <img 
                  src={slide.image} 
                  alt={slide.imageAlt}
                  className="w-full max-h-64 object-contain rounded-lg shadow-md bg-white p-2"
                />
              </div>
              <div className="md:w-1/2">
                {slide.bullets && (
                  <ul className="space-y-2">
                    {slide.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle size={18} className="text-purple-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {slide.rythme && (
              <p className="mt-4 text-sm text-purple-600 italic bg-purple-50 p-3 rounded">{slide.rythme}</p>
            )}
          </div>
        );

      case 'rythme':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {slide.items.map((item, idx) => (
                <div key={idx} className="p-4 bg-white rounded-lg shadow-md border-t-4 text-center" style={{ borderColor: item.color }}>
                  <p className="font-bold text-lg" style={{ color: item.color }}>{item.label}</p>
                  <p className="text-gray-600 mt-2">{item.freq}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'scenario':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-amber-800 mb-2">{slide.title}</h2>
            <div className="p-3 bg-amber-100 rounded-lg mb-4 inline-block">
              <p className="font-medium text-amber-800">{slide.scenario}</p>
            </div>
            <div className="space-y-3">
              {slide.steps.map((step, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 bg-white rounded-lg shadow-sm">
                  <span className="w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">{step}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'grid':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {slide.items.map((item, idx) => (
                <div key={idx} className="p-3 bg-white rounded-lg shadow-sm border border-purple-100">
                  <p className="font-bold text-purple-700">{item.name}</p>
                  <p className="text-gray-600 text-sm mt-1">{item.check}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'twoColumns':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
                <h3 className="font-bold text-purple-700 mb-3">{slide.column1.title}</h3>
                <ul className="space-y-2">
                  {slide.column1.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle size={16} className="text-purple-500" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm border border-purple-100">
                <h3 className="font-bold text-purple-700 mb-3">{slide.column2.title}</h3>
                <ul className="space-y-2">
                  {slide.column2.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700">
                      <CheckCircle size={16} className="text-purple-500" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'conclusion':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
            <h2 className="text-3xl font-bold text-white mb-6 text-center">{slide.title}</h2>
            <p className="text-white/90 mb-4 text-center">{slide.content}</p>
            <div className="max-w-2xl mx-auto">
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white">
                    <CheckCircle size={20} className="text-purple-200" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 p-4 bg-white/20 rounded-lg max-w-2xl mx-auto">
              <p className="text-white text-center">{slide.finalNote}</p>
            </div>
          </div>
        );

      default:
        return (
          <div className="p-8 h-full flex items-center justify-center">
            <p className="text-gray-500">Contenu non disponible</p>
          </div>
        );
    }
  };

  if (showQuiz) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold text-purple-800 mb-6">Quiz - Activité 9</h2>
        <p className="text-gray-600 mb-6">Testez vos connaissances sur le repérage et traitement des anomalies.</p>
        
        <div className="space-y-6">
          {quizQuestions.map((q, idx) => (
            <div key={q.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <p className="font-medium text-gray-800 mb-3">{idx + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((option, optIdx) => (
                  <button
                    key={optIdx}
                    onClick={() => handleQuizAnswer(q.id, optIdx)}
                    disabled={quizSubmitted}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      quizSubmitted
                        ? optIdx === q.correct
                          ? 'bg-green-100 border-green-500'
                          : quizAnswers[q.id] === optIdx
                            ? 'bg-red-100 border-red-500'
                            : 'bg-gray-50 border-gray-200'
                        : quizAnswers[q.id] === optIdx
                          ? 'bg-purple-100 border-purple-500'
                          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {quizSubmitted && optIdx === q.correct && <CheckCircle size={18} className="text-green-600" />}
                      {quizSubmitted && quizAnswers[q.id] === optIdx && optIdx !== q.correct && <XCircle size={18} className="text-red-600" />}
                      {option}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {!quizSubmitted ? (
          <button
            onClick={submitQuiz}
            disabled={Object.keys(quizAnswers).length < quizQuestions.length}
            className="mt-6 w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            Valider le quiz ({Object.keys(quizAnswers).length}/{quizQuestions.length} réponses)
          </button>
        ) : (
          <div className="mt-6 p-6 bg-purple-50 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-purple-800">Score : {quizScore}/{quizQuestions.length}</h3>
            <p className="text-purple-600 mt-2">
              {quizScore >= 16 ? "Excellent ! Vous maîtrisez le sujet." :
               quizScore >= 12 ? "Bon travail ! Quelques points à revoir." :
               "Continuez à étudier pour améliorer vos connaissances."}
            </p>
            <button
              onClick={() => { setShowQuiz(false); setCurrentSlide(0); setQuizAnswers({}); setQuizSubmitted(false); }}
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Revoir la présentation
            </button>
          </div>
        )}
      </div>
    );
  }

  const slide = slides[currentSlide];

  return (
    <div className="flex flex-col h-full">
      {/* Progress bar */}
      <div className="h-1 bg-gray-200">
        <div 
          className="h-full bg-purple-500 transition-all duration-300"
          style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }}
        />
      </div>

      {/* Slide content */}
      <div className="flex-1 overflow-hidden">
        {renderSlide(slide)}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between p-4 bg-white border-t">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ChevronLeft size={20} />
          Précédent
        </button>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))}
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Chargement...
              </>
            ) : isPlaying ? (
              <>
                <VolumeX size={18} />
                Arrêter
              </>
            ) : (
              <>
                <Volume2 size={18} />
                Écouter
              </>
            )}
          </button>
        </div>

        <button
          onClick={nextSlide}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'}
          <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default DEAPActivite9Presentation;
